import React, { useEffect, useMemo, useState } from 'react';

import styles from './sheetSync.module.css';
import LinkEditor from './LinkEditor';
import { deltaText, money, plainReason, problemKind, singleExact } from './sheetSyncFormat';

const FILTERS = [
  ['all', 'Все', 'Все строки, которые не удалось сопоставить'],
  ['exact', 'Есть точное совпадение', 'Есть позиция приказа с такой же ценой: привязка ничего не изменит'],
  ['differs', 'Цена другая', 'Подходящие позиции есть, но цена в таблице отличается от цены в приказе'],
  ['none', 'Нет подсказки', 'Подходящей позиции не нашлось: выберите вручную или пропустите строку'],
];

// Что случится с ценой после привязки к выбранной позиции — простыми словами
const Effect = ({ suggestion, price }) => {
  if (!suggestion) return <span className={styles.muted}>Выберите позицию, и здесь появится, что изменится</span>;
  if (suggestion.priceMatches)
    return <span className={styles.effectOk}>Цена совпадает. Ничего не изменится</span>;
  return (
    <span className={styles.effectWarn}>
      Цена в приказе станет {money(price)} вместо {money(suggestion.rulePrice)} ({deltaText(suggestion.rulePrice, price)}). Появится в
      «К применению»
    </span>
  );
};

// Строки Google-таблицы, которые система не смогла сама сопоставить с приказом. Для каждой — выбрать позицию приказа
// из подсказок (или вручную) и нажать «Привязать», либо исключить строку из синхронизации
const ProblemsTable = ({ problems, marks, busy, onLink, onIgnore }) => {
  const [filter, setFilter] = useState('all');
  const [choice, setChoice] = useState({});
  const [editorKey, setEditorKey] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(false);

  useEffect(() => {
    if (!confirmBulk) return undefined;
    const timer = setTimeout(() => setConfirmBulk(false), 6000);
    return () => clearTimeout(timer);
  }, [confirmBulk]);

  const counts = useMemo(() => {
    const c = { all: problems.length, exact: 0, differs: 0, none: 0 };
    problems.forEach((p) => {
      c[problemKind(p)] += 1;
    });
    return c;
  }, [problems]);

  const bulk = useMemo(() => problems.filter((p) => singleExact(p)), [problems]);
  const visible = filter === 'all' ? problems : problems.filter((p) => problemKind(p) === filter);

  // Выбранная позиция: то, что выбрал человек; иначе единственная с совпавшей ценой
  const picked = (p) => {
    const i = choice[p.key];
    if (i !== undefined) return i === '' ? null : p.suggestions[Number(i)];
    return singleExact(p);
  };

  const linkAll = () => {
    if (!confirmBulk) {
      setConfirmBulk(true);
      return;
    }
    setConfirmBulk(false);
    onLink(
      bulk.flatMap((p) => singleExact(p).keys.map((k) => ({ sheetKey: p.key, ...k }))),
      `Привязано строк: ${bulk.length}. Цены не менялись, привязка сохранена сразу.`
    );
  };

  if (problems.length === 0) return <div className={styles.empty}>Проблем нет: все строки таблицы сопоставлены с приказом</div>;

  return (
    <>
      <div className={styles.chips} role="group" aria-label="Фильтр проблем">
        {FILTERS.map(([key, title, hint]) => (
          <button
            key={key}
            type="button"
            title={hint}
            aria-pressed={filter === key}
            className={`${styles.chip} ${filter === key ? styles.chipActive : ''}`}
            onClick={() => setFilter(key)}
          >
            {title} <b>{counts[key]}</b>
          </button>
        ))}
      </div>

      {bulk.length > 0 && (
        <div className={styles.bulkBar}>
          <span>
            У <b>{bulk.length}</b> строк есть ровно одна позиция приказа с такой же ценой. Привязка к ней ничего не изменит, поэтому их можно
            привязать разом.
          </span>
          <button type="button" className={confirmBulk ? styles.miniDanger : styles.miniPrimary} disabled={busy} onClick={linkAll}>
            {confirmBulk ? `Точно привязать ${bulk.length}?` : `Привязать все ${bulk.length}`}
          </button>
        </div>
      )}

      <div className={styles.tableWrap}>
        <table className={styles.problemsTable}>
          <colgroup>
            <col style={{ width: '6%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '23%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '14%' }} />
          </colgroup>
          <thead>
            <tr>
              <th title="Значение колонки ID в Google-таблице">ID</th>
              <th title="Что написано в строке Google-таблицы">Строка Google-таблицы</th>
              <th className={styles.num}>Цена в таблице</th>
              <th title="Почему система не смогла сопоставить строку сама">Почему не сопоставилась</th>
              <th title="Позиция в приказе, к которой относится строка. Подсказки подобраны по марке, модели, году, комплектации и цене">
                К какой позиции приказа привязать
              </th>
              <th>Что изменится</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => {
              const sel = picked(p);
              const suggestions = p.suggestions || [];
              return (
                <React.Fragment key={p.key}>
                  <tr>
                    <td className={styles.cellKey}>{p.key}</td>
                    <td title={`${p.brand} ${p.model} ${p.year} · ${p.trim}`}>
                      {p.brand} {p.model} {p.year} · {p.trim}
                    </td>
                    <td className={styles.num}>{money(p.price)}</td>
                    <td className={styles.reasonCell}>{plainReason(p.reason)}</td>
                    <td>
                      {suggestions.length > 0 ? (
                        <select
                          className={styles.pick}
                          aria-label={`Позиция приказа для ${p.key}`}
                          value={sel ? String(suggestions.indexOf(sel)) : ''}
                          onChange={(e) => setChoice((prev) => ({ ...prev, [p.key]: e.target.value }))}
                        >
                          <option value="">— выберите позицию приказа —</option>
                          {suggestions.map((s, i) => (
                            <option key={s.label} value={i}>
                              {s.label} · {money(s.rulePrice)}
                              {s.priceMatches ? ' ✓ цена совпала' : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className={styles.muted}>Подходящей позиции не нашлось</span>
                      )}
                      <button
                        type="button"
                        className={styles.linkBtn}
                        title="Указать модель, комплектацию, год, объём и привод самому"
                        onClick={() => setEditorKey(editorKey === p.key ? null : p.key)}
                      >
                        Выбрать вручную
                      </button>
                    </td>
                    <td className={styles.effectCell}>
                      <Effect suggestion={sel} price={p.price} />
                    </td>
                    <td className={styles.cellActions}>
                      <button
                        type="button"
                        className={styles.miniPrimary}
                        disabled={busy || !sel}
                        title={
                          sel
                            ? `Сохранить привязку: ${p.key} будет брать цену из выбранной позиции`
                            : 'Сначала выберите позицию приказа в списке'
                        }
                        onClick={() =>
                          onLink(
                            sel.keys.map((k) => ({ sheetKey: p.key, ...k })),
                            `${p.key} привязана к «${sel.label}» и сохранена. Цены пока не менялись: смотрите «К применению».`
                          )
                        }
                      >
                        Привязать
                      </button>
                      <button
                        type="button"
                        className={styles.mini}
                        disabled={busy}
                        title="Исключить строку из синхронизации (вернуть можно на вкладке «Привязки»)"
                        onClick={() => onIgnore(p.key)}
                      >
                        Не синхронизировать
                      </button>
                    </td>
                  </tr>
                  {editorKey === p.key && (
                    <tr>
                      <td colSpan={7} className={styles.editorRowCell}>
                        <LinkEditor
                          sheetKey={p.key}
                          problem={p}
                          marks={marks}
                          suggestedMarkId={suggestions[0]?.markId}
                          busy={busy}
                          onSave={(payload) => {
                            setEditorKey(null);
                            onLink(payload, `${p.key} привязана и сохранена. Цены пока не менялись: смотрите «К применению».`);
                          }}
                          onCancel={() => setEditorKey(null)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {visible.length === 0 && <div className={styles.empty}>В этой группе проблем нет</div>}
      <div className={styles.hint}>
        Нужной позиции нет в списке? Нажмите «Выбрать вручную» и укажите модель, комплектацию, год, объём и привод из справочника. Строку, которая не
        относится к приказам, можно исключить кнопкой «Не синхронизировать».
      </div>
    </>
  );
};

export default ProblemsTable;
